import { useDraggable, useDroppable } from '@dnd-kit/core';
import { observer } from 'mobx-react';
import { FC, useMemo, useState } from 'react';
import { Icons } from 'src/assets/fonts/Icons/icons';
import { RESTRICTION_ACCESS } from 'src/constants/integrations';
import theme from 'src/constants/styles';
import { defaultDndDelay } from 'src/utils/dnd';
import { isDisabledByPos } from 'src/utils/helpers';
import { TOOLTIP_POSITION } from 'src/view/components/Controls/CustomTooltip/CustomTooltip';
import Icon from 'src/view/components/Controls/Icon/Icon';
import Image from 'src/view/components/Image/Image';
import IntegrationManager from 'src/view/components/IntegrationManager/IntegrationManager';
import './style.scss';

interface IProps {
  id: string;
  index?: string | number;
  image: string;
  imagesLength?: number;
  editMode?: boolean;
  disabled?: boolean;
  posRestrictions?: RESTRICTION_ACCESS;
  className?: string;
  changeEditMode: () => void;
  deleteImage: (image: string) => void;
}

const ItemImage: FC<IProps> = observer((props: IProps): JSX.Element => {
  const { attributes, isDragging, listeners, setNodeRef: draggableRef } = useDraggable({ id: props.id });
  const { isOver, setNodeRef: droppableRef } = useDroppable({ id: props.id });
  const [tooltip, setTooltip] = useState<boolean>(false);
  let timer: NodeJS.Timeout | null = null;

  const imageClickHandler = (mouseUp?: boolean) => {
    if (props.disabled) return;

    if (timer && mouseUp) {
      clearTimeout(timer);
      return;
    }

    if (!mouseUp) {
      if (isDisabledByPos(props.posRestrictions)) {
        timer = setTimeout(() => setTooltip(true), defaultDndDelay);
        return;
      }

      timer = setTimeout(props.changeEditMode, defaultDndDelay);
    }
  };

  const deleteImage = () => {
    if (props.imagesLength === 1) props.changeEditMode();
    props.deleteImage(props.image);
  };

  const availableDrag = useMemo((): boolean => {
    return Boolean(props.imagesLength && props.imagesLength > 1);
  }, [props.imagesLength]);

  const imageWrapperClassName = useMemo(() => {
    let className: string = 'full-width full-height';

    if (isDragging) {
      className += ' item-image__drag-clone';
    } else if (isOver && props.id === props.image) {
      className += ' item-image__droppable';
    }
    if (props.className) className += ` ${props.className}`;

    return className;
  }, [isDragging, isOver, props.className, props.image, props.id]);

  const getRef = () => {
    if (props.id === 'image-overlay') return undefined;
    return isDragging ? draggableRef : droppableRef;
  };

  return (
    <li ref={getRef()} className={`item-image__wrapper ${props.index ? 'ml-4' : 'item-image__wrapper_main'}`}>
      <IntegrationManager
        setting={props.posRestrictions}
        withTooltip
        tooltipOptions={{
          trigger: tooltip,
          disableTrigger: () => setTooltip(false),
          position: TOOLTIP_POSITION.PORTAL,
          wrapperClassName: 'full-width full-height',
        }}
      >
        <Image
          src={props.image}
          alt="previue_image"
          onPointerUp={!props.editMode ? () => imageClickHandler(true) : undefined}
          onPointerDown={!props.editMode ? () => imageClickHandler() : undefined}
          className="item-image"
          wrapperClassName={imageWrapperClassName}
          skeletonClassName="item-image__skeleton"
          aspectRatio="none"
        />
      </IntegrationManager>
      {props.editMode ? (
        <>
          <button onClick={deleteImage} className="item-image__btn item-image__btn-delete">
            <Icon name={Icons.Close} size={10} color={theme.colors.dark} />
          </button>
          {availableDrag ? (
            // eslint-disable-next-line react/jsx-props-no-spreading
            <button className="item-image__btn item-image__btn-drag" {...listeners} {...attributes}>
              <Icon name={Icons.DragHand} size={20} className="item-image__btn-drag-icon" />
            </button>
          ) : null}
        </>
      ) : null}
    </li>
  );
});

export default ItemImage;
